// CustomSelect.tsx
'use client';

import { useState, useRef, useEffect } from 'react';
import styles from './CustomSelect.module.css';

export type OptionType = {
  label: string;
  value: number;
};

export type CustomSelectProps = {
  instanceId: string;
  isMulti: boolean;
  options: OptionType[];
  value: OptionType | OptionType[] | null;
  onChange: (selected: OptionType | OptionType[] | null) => void;
  placeholder: string;
  isDisabled?: boolean;
  isSearchable?: boolean;
  className?: string;
};

export default function CustomSelect({
  instanceId,
  isMulti,
  options,
  value,
  onChange,
  placeholder,
  isDisabled = false,
  isSearchable = false,
  className = ""
}: CustomSelectProps) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!isDisabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (option: OptionType) => {
    if (isMulti) {
      const currentValue = Array.isArray(value) ? value : [];
      const isSelected = currentValue.some(v => v.value === option.value);
      
      if (isSelected) {
        onChange(currentValue.filter(v => v.value !== option.value));
      } else {
        onChange([...currentValue, option]);
      }
    } else {
      onChange(option);
      setIsOpen(false);
    }
  };

  const handleRemoveValue = (optionToRemove: OptionType, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isMulti && Array.isArray(value)) {
      onChange(value.filter(v => v.value !== optionToRemove.value));
    }
  };

  const isSelected = (option: OptionType) => {
    if (isMulti && Array.isArray(value)) {
      return value.some(v => v.value === option.value);
    }
    return value && !Array.isArray(value) && value.value === option.value;
  };

  const getDisplayValue = () => {
    if (!value) return null;
    
    if (isMulti && Array.isArray(value)) {
      return value.map(v => (
        <div key={v.value} className={styles.multiValue}>
          <span>{v.label}</span>
          <button
            type="button"
            className={styles.multiValueRemove}
            onClick={(e) => handleRemoveValue(v, e)}
            aria-label={`Remover ${v.label}`}
          >
            ×
          </button>
        </div>
      ));
    }
    
    if (!Array.isArray(value)) {
      return <span className={styles.singleValue}>{value.label}</span>;
    }
    
    return null;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isDisabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen && focusedIndex >= 0) {
          handleOptionClick(options[focusedIndex]);
        } else {
          setIsOpen(true);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setFocusedIndex(prev => 
            prev < options.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
    }
  };

  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && menuRef.current) {
      const focusedElement = menuRef.current.children[focusedIndex] as HTMLElement;
      focusedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex, isOpen]);

  const displayValue = getDisplayValue();
  const hasValue = (isMulti && Array.isArray(value) && value.length > 0) || 
                   (!isMulti && value && !Array.isArray(value));

  // Filtragem das opções conforme o texto digitado
  const filteredOptions = isSearchable && search.length > 0
    ? options.filter(option => option.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div 
      ref={containerRef} 
      className={`${styles.customSelect} ${isDisabled ? styles.disabled : ''} ${className}`.trim()}
    >
      <div
        className={`${styles.control} ${isOpen ? styles.controlFocused : ''}`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={isDisabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-labelledby={instanceId}
      >
        <div className={styles.valueContainer}>
          {hasValue ? (
            displayValue
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>
        <div className={styles.indicators}>
          <span className={`${styles.dropdownIndicator} ${isOpen ? styles.indicatorRotated : ''}`}>
            <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path></svg>
          </span>
        </div>
      </div>

      {isOpen && (
        <div 
          ref={menuRef}
          className={styles.menu}
          role="listbox"
        >
          {isSearchable && (
            <input
              className={styles.inputSearchSelect}
              type="text"
              placeholder="Buscar..."
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setFocusedIndex(0);
              }}
              autoFocus
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
            />
          )}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option.value}
                className={`${styles.option} ${isSelected(option) ? styles.optionSelected : ''} ${
                  focusedIndex === index ? styles.optionFocused : ''
                }`}
                onClick={() => handleOptionClick(option)}
                onMouseEnter={() => setFocusedIndex(index)}
                role="option"
                aria-selected={isSelected(option) ? true : false}
              >
                {option.label}
              </div>
            ))
          ) : (
            <div className={styles.option}>Nenhuma opção encontrada</div>
          )}
        </div>
      )}
    </div>
  );
}